import { NotificationProvider } from '@/contexts/NotificationContext.tsx';
import { EXTERNAL_ROLES, INTERNAL_ROLES } from '@/lib/constants.ts';
import { CustomRouteGuard } from '@/lib/custom-route-guard.tsx';
import ErrorBoundary from '@/pages/ErrorBoundary.tsx';
import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import ProgressDialog from './components/compositions/progress-dialog.tsx';
import './i18n';
import { QueryContext } from './contexts/QueryContext.tsx';
import { UserProvider } from './contexts/UserContext.tsx';
import { useTheme } from './hooks/useTheme.tsx';
import { AllowHomeRedirect, applicationLoadStatus, loadOnePlaceScript } from './lib/oneplace.ts';
import Loading from './loading.tsx';
import './styles/global.css';


const AccessRestricted = lazy(() => import('./pages/AccessRestricted.tsx'));
const UserEdit = lazy(() => import('./pages/system-settings/users/user-edit.tsx'));
const Users = lazy(() => import('./pages/system-settings/users/users.tsx'));
const UserNotFound = lazy(() => import('./pages/UserNotFound.tsx'));
const ErrorPage = lazy(() => import('./pages/ErrorPage.tsx'));
const CustomErrorPage = lazy(() => import('./pages/CustomErrorPage.tsx'));
const ClientEdit = lazy(() => import('./pages/system-settings/clients/ClientEdit.tsx'));
const Clients = lazy(() => import('./pages/system-settings/clients/Clients.tsx'));
const Home = lazy(() => import('./pages/home/Home.tsx'));
const InterfaceExamples = lazy(
        () => import('./pages/system-settings/Regions/InterfaceExamples.tsx'),
);
const Regions = lazy(() => import('./pages/system-settings/Regions/Regions.tsx'));
const SystemLayout = lazy(() => import('./pages/system-settings/System-Layout.tsx'));
const RegionEditPage = lazy(() => import('./pages/system-settings/Regions/RegionEditPage.tsx'));
const RegionAddPage = lazy(() => import('./pages/system-settings/Regions/RegionAddPage.tsx'));
const LanguagesPage = lazy(() => import('./pages/system-settings/languages/LanguagesPage.tsx'));
const LanguageEditPage = lazy(
        () => import('./pages/system-settings/languages/LanguageEditPage.tsx'),
);
const MissingTranslationsPage = lazy(
        () => import('./pages/system-settings/languages/MissingTranslationsPage.tsx'),
);
const Logs = lazy(() => import('./pages/system-settings/logs.tsx'));

const router = createBrowserRouter([
    {
        path : '*',
        element : <Navigate to="/" replace/>,
    },
    {
        path : '/accessRestricted',
        element : <AccessRestricted/>,
    },
    {
        path : '/',
        errorElement: <CustomErrorPage />,
    children: [
            {
                path : '/',
                element : <Home/>,
                errorElement : <ErrorPage/>,
            },
            {
                path : '/error',
                element : <ErrorPage/>,
            },
            {
                path : '/error/redirect',
                element : <CustomErrorPage/>,
            },
            {
                path : '/system',
                element : <SystemLayout/>,
                children : [
                    {
                        path : '/system',
                        element : (
                                <CustomRouteGuard notAllowedClaims={EXTERNAL_ROLES}>
                                    <Clients/>
                                </CustomRouteGuard>
                        ),
                    },

                    {
                        path : '/system/clients',
                        element : (
                                <CustomRouteGuard notAllowedClaims={EXTERNAL_ROLES}>
                                    <Clients/>
                                </CustomRouteGuard>
                        ),
                    },
                    {
                        path : '/system/clients/edit/:id',
                        element : (
                                <CustomRouteGuard notAllowedClaims={EXTERNAL_ROLES}>
                                    <ClientEdit/>
                                </CustomRouteGuard>
                        ),
                    },
                    {
                        path : '/system/users',
                        element : (
                                <CustomRouteGuard notAllowedClaims={EXTERNAL_ROLES}>
                                    <Users/>
                                </CustomRouteGuard>
                        ),
                    },
                    {
                        path : '/system/users/edit/:id',
                        element : (
              <CustomRouteGuard notAllowedClaims={EXTERNAL_ROLES}>
                                    <UserEdit/>
                                </CustomRouteGuard>
                        ),
                    },
                    {
                        path: '/system/regions',
                        element: (
                            <CustomRouteGuard notAllowedClaims={[...EXTERNAL_ROLES, ...INTERNAL_ROLES]}>
                                <Regions />
                            </CustomRouteGuard>
                        ),
                    },
                    {
                        path: '/system/regions/add',
                        element: (
                            <CustomRouteGuard notAllowedClaims={[...EXTERNAL_ROLES, ...INTERNAL_ROLES]}>
                                <RegionAddPage />
                            </CustomRouteGuard>
                        ),
                    },
                    {
                        path: '/system/regions/edit/:id',
                        element: (
                            <CustomRouteGuard notAllowedClaims={[...EXTERNAL_ROLES, ...INTERNAL_ROLES]}>
                                <RegionEditPage />
                            </CustomRouteGuard>
                        ),
                    },
                    {
                        path: '/system/languages',
                        element: (
                            <CustomRouteGuard notAllowedClaims={[...EXTERNAL_ROLES, ...INTERNAL_ROLES]}>
                                <LanguagesPage />
                            </CustomRouteGuard>
                        ),
                    },
                    {
                        path: '/system/languages/edit/:id',
                        element: (
                            <CustomRouteGuard notAllowedClaims={[...EXTERNAL_ROLES, ...INTERNAL_ROLES]}>
                                <LanguageEditPage />
                            </CustomRouteGuard>
                        ),
                    },
                    {
                        path: '/system/languages/missing-translations/:id',
                        element: (
                            <CustomRouteGuard notAllowedClaims={[...EXTERNAL_ROLES, ...INTERNAL_ROLES]}>
                                <MissingTranslationsPage />
                            </CustomRouteGuard>
                        ),
                    },
                    {
                        path: '/system/logs',
                        element: (
                            <CustomRouteGuard notAllowedClaims={[...EXTERNAL_ROLES, ...INTERNAL_ROLES]}>
                                <Logs />
                            </CustomRouteGuard>
                        ),
                    },
                    {
                        path : '/system/ui',
                        element : <InterfaceExamples/>,
                    },
                ],
            },
        ],
    },
]);

export default function App() {
    const [onePlaceFinished, setOnePlaceFinished] = React.useState(applicationLoadStatus.Loading);
    const [user, setUser] = React.useState();
  const {} = useTheme();

    AllowHomeRedirect();
    
    

    React.useEffect(() => {
        loadOnePlaceScript(setOnePlaceFinished, setUser);
    }, []);

    switch (onePlaceFinished) {
        case applicationLoadStatus.Loading:
            return <Loading/>;
        case applicationLoadStatus.UserNotFound:
            return <UserNotFound/>;
        case applicationLoadStatus.AccessRestricted:
            return <AccessRestricted/>;
        case applicationLoadStatus.Error:
            return <CustomErrorPage/>;
        default:
            return (
                <div>
                    <React.StrictMode>
                        <UserProvider user={user}>
                            <QueryContext>
                                    <NotificationProvider>
                                        <ProgressDialog/>
                                        <ErrorBoundary>
                                            <RouterProvider router={router} />
                                        </ErrorBoundary>
                                    </NotificationProvider>
                            </QueryContext>
                        </UserProvider>
                    </React.StrictMode>
                </div>
            );
    }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
        <Suspense fallback={<Loading/>}>
            <App/>
        </Suspense>,
);
